import { useCallback, useEffect, useRef, useState } from 'react';

// Contexts / Services
import { useDocumentStore } from '../stores/documentStore';
import { useDocumentSelectionStore } from '../stores/documentSelectionStore';
import { useChatListStore } from '../stores/chatListStore';
import { apiClient } from '../clients/APIClient';

// Types
import { SearchableDocument, FileInfo } from '../types/models';

// Hooks
import { useDebounce } from '../hooks/useDebounce';

// Common
import { ModalPortal } from './common/ModalPortal';




interface SearchModalProps {
	query: string;
	onSubmit: (query: string) => void;
	onCancel: () => void;
	isOpen: boolean;
}

/**
 * Modal for searching chats and documents
 *
 * Document Selection Behavior:
 * - Selected documents PERSIST when opening/closing search modal
 * - Allows users to search for more documents to add to their selection
 * - "New Chat with X documents" preserves selections (doesn't clear on new chat)
 */
export function SearchModal({
	query,
	onSubmit,
	onCancel,
	isOpen,
}: SearchModalProps) {
    const inputRef = useRef<HTMLInputElement | null>(null);
    const containerRef = useRef<HTMLDivElement | null>(null);

    const [newQuery, setNewQuery] = useState(query);
    const hasText = newQuery.trim().length > 0; // for clear/close button
    const debouncedQuery = useDebounce(newQuery, 200);
    const [results, setResults] = useState<SearchableDocument[]>([]);
    const [isLoading, setIsLoading] = useState(false);

    const selectChat = useChatListStore((s) => s.selectChat);
    const onNewChat = useChatListStore((s) => s.onNewChat);
    const documents = useDocumentStore((s) => s.documents);
    const { addDocument, selectedFilesDoc, setSelectedFiles } = useDocumentSelectionStore();

    // Keyboard navigation state for results
    const [selectedIndex, setSelectedIndex] = useState(0);
    const itemRefs = useRef<Array<HTMLElement | null>>([]);

    // Reset query and selection when modal opens
    useEffect(() => {
        if (isOpen) {
            setNewQuery(query);
            setSelectedIndex(0);
        }
    }, [isOpen, query]);

    // Perform search when debounced query changes
    useEffect(() => {
        let cancelled = false;

        const runSearch = async () => {
            if (!isOpen || debouncedQuery.trim().length === 0) {
                if (!cancelled) {
                    setResults([]);
                    setIsLoading(false);
                }
                return;
            }

            setIsLoading(true);
            try {
                const searchResults = await apiClient.search.query(debouncedQuery);
                if (!cancelled) {
                    setResults(searchResults);
                }
            } catch (err) {
                if (!cancelled) {
                    console.error('[SearchModal] Search failed:', err);
                    setResults([]);
                }
            } finally {
                if (!cancelled) {
                    setIsLoading(false);
                }
            }
        };

        void runSearch();

        return () => {
            cancelled = true;
        };
    }, [isOpen, debouncedQuery]);

    const handleCancel = useCallback(() => {
        onCancel();
    }, [onCancel]);

    // Dual function clear or close button
    const handleClearOrClose = () => {
        if (hasText) {
            setNewQuery('');
            requestAnimationFrame(() => inputRef.current?.focus());
            setSelectedIndex(0);
            return;
        }
        handleCancel();
    };

    // Focus search input and lock scroll on open
    useEffect(() => {
        if (!isOpen) return;
        requestAnimationFrame(() => inputRef.current?.focus());
        document.body.classList.add('overflow-hidden');
        return () => {
            document.body.classList.remove('overflow-hidden');
        };
    }, [isOpen]);

    // Scroll the selected item into view when selection changes
    useEffect(() => {
        if (!isOpen) return;
        const el = itemRefs.current[selectedIndex];
        if (el && typeof el.scrollIntoView === 'function') {
            el.scrollIntoView({ block: 'nearest' });
        }
    }, [selectedIndex, isOpen]);

    // Handle New Chat action
    // NOTE: We preserve selected documents when creating new chat from search.
    // Decision: Search modal with selected docs -> New Chat should KEEP those docs selected.
    // This is the intended workflow: select docs in search, then create new chat with them.
    const handleNewChat = useCallback(() => {
        onNewChat();
        // Re-select the documents after new chat is created
        setSelectedFiles(selectedFilesDoc);
        handleCancel();
    }, [onNewChat, handleCancel, selectedFilesDoc, setSelectedFiles]);

    const handleSelect = useCallback((index: number) => {
        const selectedResult = results[index];
        if (!selectedResult) return;

        const actualId = selectedResult.id.replace(/^(chat_|file_)/, '');

        if (selectedResult.sourceType === 'chat') {
            selectChat(actualId);
            handleCancel(); // close on select

            // TODO : ? Add preview of chat metadata ?
            // - Show chat metadata (date, message count)
            // - Require double-click or "Open" button to confirm
        } else if (selectedResult.sourceType === 'file') {
            // Toggle document selection, keep modal open for multi-select
            addDocument(actualId);
            // Modal stays open - user closes manually (Esc/X) when done
        }
    }, [results, selectChat, handleCancel, addDocument]);

    // Global keyboard handling: Escape to close, Arrow keys to navigate, Enter to select/submit, Ctrl+N for new chat
    useEffect(() => {
        if (!isOpen) return;
        const onKey = (e: KeyboardEvent) => {
            // Ctrl+N to create new chat with selected documents
            if (
                (e.ctrlKey || e.metaKey) &&
                e.key === 'n' &&
                selectedFilesDoc.length > 0
            ) {
                e.preventDefault();
                handleNewChat();
                return;
            }

            if (e.key === 'Escape') {
                e.preventDefault();
                handleCancel();
                return;
            }
            if (e.key === 'ArrowDown') {
                e.preventDefault();
                if (results.length > 0) {
                    setSelectedIndex((prev) => (prev + 1) % results.length);
                }
                return;
            }
            if (e.key === 'ArrowUp') {
                e.preventDefault();
                if (results.length > 0) {
                    setSelectedIndex(
                        (prev) => (prev - 1 + results.length) % results.length,
                    );
                }
                return;
            }
            if (e.key === 'Enter') {
                // Submit query or select result
                e.preventDefault();
                if (
                    results.length > 0 &&
                    selectedIndex >= 0 &&
                    selectedIndex < results.length
                ) {
                    handleSelect(selectedIndex);
                } else {
                    onSubmit(newQuery);
                }
                
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [
        isOpen,
        newQuery,
        selectedIndex,
        results.length,
        selectedFilesDoc.length,
        handleCancel,
        handleNewChat,
        handleSelect,
        onSubmit,
    ]);

    // Rendering functions
    const renderResultItem = (item: SearchableDocument, index: number) => {
        const isChat = item.sourceType === 'chat';
        const isDocument = item.sourceType === 'file';
        const actualId = item.id.replace(/^(chat_|file_)/, '');
        const isDocumentSelected =
            isDocument && selectedFilesDoc.includes(actualId);
        const selected = index === selectedIndex;

        const selectionClass = selected ? 'bg-neutral-700 rounded-md' : '';
        const documentSelectedClass = isDocumentSelected ? 'bg-red-600' : '';

        return (
            <div
                key={item.id}
                id={`search-result-${index}`}
                className="search-result-item-wrapper"
                role="option"
                aria-selected={selected}
                onMouseEnter={() => setSelectedIndex(index)}
                onClick={() => handleSelect(index)}
                ref={(el) => {
                    itemRefs.current[index] = el;
                }}
                tabIndex={-1}
                title={item.title}
            >
                <span
                    className={`search-result-indicator ${selected ? 'active' : ''}`}
                 />
                <div
                    className={`search-result-item-content ${selectionClass} ${documentSelectedClass}`}
                >
                    <i
                        className={`search-result-item-icon fas ${isChat ? 'fa-comments' : 'fa-file'} ${
                            isDocumentSelected
                                ? 'text-neutral-100'
                                : 'text-red-600'
                        }`}
                     />
                    <span className="overflow-hidden text-ellipsis whitespace-nowrap">
                        {item.title}
                    </span>
                </div>
            </div>
        );
    };

    /* Used in actions section */
    const renderSelectedDocument = (docId: string) => {
        // Search in results (SearchableDocument[]) first
        const doc = results.find((d: SearchableDocument) => d.id === docId);
        if (doc) {
            return (
                <div
                    key={docId}
                    className="search-result-item-wrapper"
                    onClick={() => addDocument(docId)}
                    title={`${doc.title} (click to deselect)`}
                >
                    <div className="search-result-item-content bg-red-600 rounded-md">
                        <i className="search-result-item-icon fas fa-file text-neutral-100" />
                        <span className="overflow-hidden text-ellipsis whitespace-nowrap">
                            {doc.title}
                        </span>
                    </div>
                </div>
            );
        }
        
        // Fallback to documents (FileInfo[]) if not in results
        const fileDoc = documents.find((d: FileInfo) => d.id === docId);
        if (!fileDoc) return null;

        return (
            <div
                key={docId}
                className="search-result-item-wrapper"
                onClick={() => addDocument(docId)}
                title={`${fileDoc.filename} (click to deselect)`}
            >
                <div className="search-result-item-content bg-red-600 rounded-md">
                    <i className="search-result-item-icon fas fa-file text-neutral-100" />
                    <span className="overflow-hidden text-ellipsis whitespace-nowrap">
                        {fileDoc.filename}
                    </span>
                </div>
            </div>
        );
    };

    /* Used in actions section */
    const renderNewChatButton = () => (
        <div
            className="search-result-item-wrapper"
            onClick={handleNewChat}
            title={`Create new chat with ${selectedFilesDoc.length} selected document${selectedFilesDoc.length > 1 ? 's' : ''}`}
        >
            <div className="search-result-item-content bg-neutral-700 rounded-md">
                <i className="search-result-item-icon fas fa-plus text-red-600" />
                <span className="overflow-hidden text-ellipsis whitespace-nowrap">
                    New Chat with {selectedFilesDoc.length} document
                    {selectedFilesDoc.length > 1 ? 's' : ''}
                </span>
            </div>
        </div>
    );

    // Get selected documents that aren't already visible in search results
    const getSelectedDocsNotInResults = () => {
        const resultIds = new Set(
            results.map((r) => r.id.replace(/^file_/, '')),
        );
        return selectedFilesDoc.filter((docId: string) => !resultIds.has(docId));
    };

    return (
        <ModalPortal isOpen={isOpen}>
            {/* overlay */}
            <div
                className="modal-overlay flex items-center justify-center"
                onClick={handleCancel}
            >
                {/* container */}
                <div
                    className="modal-container modal-container-compact relative flex flex-col"
                    onClick={(e) => e.stopPropagation()}
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="search-modal-title"
                    ref={containerRef}
                >
                    <h2 id="search-modal-title" className="sr-only">
                        Search
                    </h2>

                    {/* input container */}
                    <div className="search-input-container">
                        <input
                            className="search-input"
                            value={newQuery}
                            onChange={(e) => {
                                setNewQuery(e.target.value);
                                setSelectedIndex(0);
                            }}
                            placeholder="Search..."
                            spellCheck="false"
                            enterKeyHint="done"
                            type="text"
                            id="search-input"
                            ref={inputRef}
                            role="combobox"
                            aria-autocomplete="list"
                            aria-controls="search-results-list"
                            aria-expanded={results.length > 0}
                            aria-activedescendant={`search-result-${selectedIndex}`}
                        />
                        <button
                            className={`modal-close-clear-btn ${hasText ? 'active' : ''}`}
                            onClick={handleClearOrClose}
                            aria-label={
                                hasText ? 'Clear search' : 'Close search'
                            }
                            title={hasText ? 'Clear search' : 'Close search'}
                            type="button"
                        >
                            <i className="fas fa-times" />
                        </button>
                    </div>
                    <hr className="search-divider" />
                    {/* Results container */}
                    <div
                        className="search-results"
                        id="search-results-list"
                        role="listbox"
                        aria-label="Search results"
                    >
                        {isLoading ? (
                            <p className="text-neutral-400 text-center p-4">Searching...</p>
                        ) : results.length > 0 ? (
                            results.map((item, i) => renderResultItem(item, i))
                        ) : (
                            <p className="text-neutral-400 text-center p-4">
                                {newQuery.trim().length > 0
                                    ? 'No results found.'
                                    : 'Search for chats and documents.'}
                            </p>
                        )}
                    </div>

                    {/* Pinned actions section - Selected documents and "New Chat" */}
                    {selectedFilesDoc.length > 0 && (
                        <>
                            <hr className="search-divider p-0.5" />
                            <div className="search-actions">
                                {getSelectedDocsNotInResults().map(
                                    renderSelectedDocument,
                                )}
                                {renderNewChatButton()}
                            </div>
                        </>
                    )}

                    <hr className="search-divider pb-0" />
                    {/* instructions */}
                    <div className="search-footer" aria-hidden="true">
                        {/* Instructions items */}
                        <div className="search-footer-item">
                            <span className="search-footer-command">↑↓</span>
                            <span>to navigate</span>
                        </div>

                        <div className="search-footer-item">
                            <span className="search-footer-command">↵</span>
                            <span>to open / select</span>
                        </div>

                        {selectedFilesDoc.length > 0 && (
                            <div className="search-footer-item">
                                <span className="search-footer-command">
                                    ^N
                                </span>
                                <span>new chat</span>
                            </div>
                        )}

                        <div className="search-footer-item">
                            <span className="search-footer-command">esc</span>
                            <span>to dismiss</span>
                        </div>
                    </div>
                </div>
            </div>
        </ModalPortal>
    );
}

